import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Column } from '@/components/ui/layout/Column';
import { HandleToggleChangeProps } from '@/features/analysis/types/canvas.types';
import type { FileDetection } from '../../../../../types/api.types';
import { DetectionIcon } from '@/components/ui/icons/DetectionIcon';
import { Row } from '@/components/ui/layout/Row';
import { Text } from '@/components/ui/base/Text';
import { DetectionAccordion } from './DetectionAccordion';
import { DetectionsPanelSkeleton } from './DetectionsPanelSkeleton';
interface DetectionsPanelProps {
  detections: FileDetection[];
  setPageIndex: (index: number) => void;
  setHoverDetection: Dispatch<SetStateAction<string | undefined>>;
  hideDetectionCanvas?: boolean;
  hoverDetectionDocTitle?: string;
  pageIndex: number;
  handleToggleChange: HandleToggleChangeProps;
  hoverDetection?: string | undefined;
  selectedMrz?: string | undefined;
  selectedMrzId?: string;
  /**
   * If true, the detections panel will be hidden, but we still need to keep the state
   */
  hidden?: boolean;
  isLoading?: boolean;
}

export const DetectionsPanel = ({
  detections,
  pageIndex,
  setPageIndex,
  setHoverDetection,
  hoverDetection,
  hideDetectionCanvas,
  handleToggleChange,
  hidden,
  selectedMrzId,
  isLoading,
}: DetectionsPanelProps) => {
  const { detectionsByPage } = useMemo(() => {
    const byPage: Record<number, FileDetection[]> = {};

    detections.forEach((detection) => {
      if (typeof detection.page === 'number' && !isNaN(detection.page)) {
        if (!byPage[detection.page]) byPage[detection.page] = [];
        byPage[detection.page].push(detection);
      } else {
        if (!byPage[-1]) byPage[-1] = [];
        byPage[-1].push(detection);
      }
    });
    return { detectionsByPage: byPage };
  }, [detections]);

  // Récupérer les pages triées, -1 (non localisables) en dernier
  const sortedPages = useMemo(() => {
    const pages = Object.keys(detectionsByPage).map(Number);
    return pages.sort((a, b) => (a === -1 ? 1 : b === -1 ? -1 : a - b));
  }, [detectionsByPage]);

  // Accordéons ouverts par page
  const [openPages, setOpenPages] = useState<Record<string, boolean>>({
    '-1': true,
  });

  const togglePageAccordion = (key: string) => {
    setOpenPages((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    // On ouvre tous les accordéons par défaut
    setOpenPages(() => {
      const allPagesOpen = sortedPages.reduce(
        (acc, key) => {
          acc[key] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
      allPagesOpen[-1] = true;
      return allPagesOpen;
    });
  }, [sortedPages]);

  if (isLoading) {
    return <DetectionsPanelSkeleton />;
  }

  return (
    <Column gap="md" className={hidden ? 'hidden' : ''} id="detections-section">
      <Row justify="start">
        <DetectionIcon
          size={24}
          className="fill-neutralText dark:fill-neutralText-dark"
        />
        <Text size="lg" weight="medium">
          Détections
        </Text>
      </Row>
      <Column>
        {sortedPages.length > 0 ? (
          sortedPages.map((page) => (
            <DetectionAccordion
              key={page}
              page={page}
              detections={detectionsByPage[page]}
              isOpen={!!openPages[page]}
              onToggle={() => togglePageAccordion(page.toString())}
              setPageIndex={setPageIndex}
              setHoverDetection={setHoverDetection}
              hoverDetection={hoverDetection}
              pageIndex={pageIndex}
              handleToggleChange={handleToggleChange}
              hideDetectionCanvas={hideDetectionCanvas}
              selectedMrzId={selectedMrzId}
            />
          ))
        ) : (
          <Column>
            <Row>
              <Text weight="light">Aucune détection trouvée.</Text>
            </Row>
          </Column>
        )}
      </Column>
    </Column>
  );
};
